import { Injectable } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { StoragePathProvider } from "../../../providers/storage-path/storage-path";
import { RequestBalanceDetail } from "../../../shared/nestjs/gd-integrated/dto/leave.dto";
import { LeaveCalendarApi } from "../../../shared/nestjs/gd-integrated/services/leave-calendar.api";
import { LeaveSf6Api } from "../../../shared/nestjs/gd-integrated/services/leave-sf6.api";
import { NestResponsesProvider, ResponsePaging } from "../../../shared/nestjs/nest-responses";
import { LeaveApi } from "../../../shared/nestjs/gd-only/services/leave.api";
import { SfLeaveRequestApi } from "../../../shared/nestjs/gd-only/services/sf-leave-request.api";
import { RequestPaginationDto, RequestCheckEmployeeOptionDto } from "../../../shared/nestjs/gd-only/dto/sf-leave-request.dto";
import { AttendanceApi } from "../../../shared/nestjs/gd-only/services/attendance.api";
import { RequestGetUserAttendanceWeekDto, ResponseGetUserAttendanceWeekDto } from "../../../shared/nestjs/gd-only/dto/attendance.dto";

@Injectable()
export class LeaveBloc {
	private _subscription: Array<Subscription> = [];
	constructor(
		private leaveSf6Api: LeaveSf6Api,
		private leaveCalendarApi: LeaveCalendarApi,
		private nestResponsesProvider: NestResponsesProvider,
		private storagePathProvider: StoragePathProvider,
		private leaveApi: LeaveApi,
		private sfLeaveRequestNestApi: SfLeaveRequestApi,
		private attendanceApi: AttendanceApi
	) { }

	dispose() {
		this._subscription.forEach(sub => sub.unsubscribe());
	}

	// Get Calendar
	getCalendarLeave(data: any): Observable<any> {
		return this.leaveCalendarApi.getCalendarLeave(data).pipe(
			map(params => {
				const colorNote = [];
				for (const item of params.data) {
					colorNote.push({
						date: item,
						event: [
							{
								color: '#F0624D',
							}
						]
					});
				}
				return colorNote;
			})
		)
	}

	// Get Detail Calendar
	getCalendarLeaveDetail(data: any): Observable<any> {
		return this.leaveCalendarApi.getCalendarLeaveDetail(data).pipe(
			map(params => {
				return params.data;
			})
		)
	}

	async getBalanceDetail(params: RequestBalanceDetail) {
		try {
			const data = await this.leaveSf6Api.leaveBalanceDetail(params).toPromise();
			return data.data;
		} catch (error) {
			if (typeof error === 'string') {
				throw error;
			} else {
				const message = this.nestResponsesProvider.handleMsgError(error);
				throw message;
			}
		}
	}

	async formatCalendarLeaveDetail(data: any): Promise<any> {
		const dataLeave = [];
		for (const item of data) {
			const path = await this.storagePathProvider.getStorageProfileCustom(item.userId);
			dataLeave.push({
				noteType: item.leaveName,
				noteLabel: item.fullName,
				noteImage: `${path.urlDownload}${item.photo}`,
				statusLabel: item.status,
				statusColor: '#2BB34D',
				requestNo: item.requestNo,
        dayType: this.dayType(item.dayType),
			});
		}
		return {
			date: data,
			event: (dataLeave.length > 0) ? [
				{
					group: 'Teammates Leave',
					color: '#F0624D',
					data: dataLeave
				}
			] : []
		}
	}

	updateLeaveBalance(payload) {
		return new Promise((resolve, reject) => {
			this.leaveApi.updateLeaveBalance(payload)
				.subscribe(result => {
					resolve(result);
				}, error => {
					reject(error);
				})
		})
	}

	checkEmployeeOption(payload: RequestCheckEmployeeOptionDto, pagination: RequestPaginationDto) {
		return new Promise<ResponsePaging<any>>((resolve, reject) => {
			this.sfLeaveRequestNestApi.checkEmployeeOption(payload, pagination)
				.subscribe(result => {
					resolve(result);
				}, error => {
					reject(error);
				});
		})
	}

	getEmpShift(payload: RequestGetUserAttendanceWeekDto) {
    return this.attendanceApi.getUserAttendanceWeekNew(payload)
      .pipe(map(result => this.createLeaveTime(result, payload)));
  }

  private createLeaveTime(result: ResponseGetUserAttendanceWeekDto, payload: RequestGetUserAttendanceWeekDto) {
    if (result.info.length) {
      const data = result.info[0];
      const startDate = payload.startDate.substring(0, 10);
      const endDate = payload.endDate.substring(0, 10);
      let leaveTimeFrom = null;
      let leaveTimeTo = null;

      if (data.shiftstarttime) {
        const index = data.shiftstarttime.indexOf('T');
        const hourShiftStart = data.shiftstarttime.substring(index);
        leaveTimeFrom = startDate + hourShiftStart;
      }

      if (data.shiftendtime) {
        const index = data.shiftendtime.indexOf('T');
        const hourShiftEnd = data.shiftendtime.substring(index);
        leaveTimeTo = endDate + hourShiftEnd;
      }

      return { leaveTimeFrom, leaveTimeTo };
    }
  }

  dayType(e) {
    if (e === 'FD') {
      return 'Full Day';
    } else if (e === 'PD') {
      return 'Part of Day';
    } else if (e === 'HD') {
      return 'Half Day';
    }
  }
}



// WEBPACK FOOTER //
// ./src/bloc/hr/leave/leave.bloc.ts